import { useId, useRef, useState } from 'react'
import { Button, Label, Spinner } from '@heroui/react'

import { AlertIcon, PlusIcon, TrashIcon } from '@/components/ui/icons'
import type { FileRef } from '@/lib/api/common'
import { errorMessage } from '@/lib/api/api-error'

import { useFileUpload } from '../hooks'
import { imageFileError } from '../image-validation'

interface ImageFieldProps {
  label: string
  description?: string
  /** The image currently attached, as the API returned it. `null` while empty. */
  value: FileRef | null
  /**
   * Called with the uploaded file, or `null` when the merchant clears it.
   *
   * The caller keeps the whole `FileRef` rather than just the id: it needs the id
   * to save and the url to keep showing the preview after the picker closes.
   */
  onChange: (file: FileRef | null) => void
  isDisabled?: boolean
  /** Preview box aspect. A banner is wide; a logo is square. */
  aspect?: 'square' | 'wide'
  /**
   * Takes the label out of the layout but leaves it on the input, for the places
   * something else already names the field — the title of the card it is alone
   * inside. Never a way to drop it: the file input would otherwise be unnamed.
   */
  isLabelHidden?: boolean
}

/**
 * Pick an image, upload it, keep the id.
 *
 * Upload happens on selection rather than on submit. The endpoints that take an
 * image take an id of a file that already exists, so deferring the upload would
 * mean the save could not be one request — and a merchant who picked a photo and
 * pressed Save would be waiting on two round trips with nothing on screen.
 */
export function ImageField({
  label,
  description,
  value,
  onChange,
  isDisabled,
  aspect = 'wide',
  isLabelHidden,
}: ImageFieldProps) {
  const inputId = useId()
  const inputRef = useRef<HTMLInputElement>(null)
  const { upload, isUploading, error } = useFileUpload()
  const [localError, setLocalError] = useState<string | null>(null)

  async function handlePick(file: File | undefined) {
    if (!file) return

    setLocalError(null)

    const invalid = imageFileError(file)

    if (invalid) {
      setLocalError(invalid)
      return
    }

    try {
      onChange(await upload(file))
    } catch {
      // `error` off the mutation carries the server's wording; nothing to add.
    } finally {
      // Clearing lets the same file be picked again after a failure — the input
      // fires no change event when the selection has not changed.
      if (inputRef.current) inputRef.current.value = ''
    }
  }

  const message = localError ?? (error ? errorMessage(error, 'Upload failed.') : null)

  return (
    <div className="flex flex-col gap-1.5">
      <Label htmlFor={inputId} className={isLabelHidden ? 'sr-only' : undefined}>
        {label}
      </Label>

      <div className="flex items-center gap-3">
        <div
          className={`border-border bg-surface relative flex shrink-0 items-center justify-center overflow-hidden rounded-xl border ${
            aspect === 'square' ? 'size-20' : 'h-20 w-36'
          }`}
        >
          {value ? (
            <img src={value.url} alt="" className="size-full object-cover" />
          ) : (
            <span className="text-muted text-[11px]">No image</span>
          )}

          {isUploading && (
            <span className="bg-background/70 absolute inset-0 flex items-center justify-center">
              <Spinner size="sm" />
            </span>
          )}
        </div>

        <div className="flex flex-col gap-2">
          <input
            ref={inputRef}
            id={inputId}
            type="file"
            accept="image/*"
            className="sr-only"
            disabled={isDisabled || isUploading}
            onChange={(event) => void handlePick(event.target.files?.[0])}
          />

          <Button
            variant="ghost"
            size="sm"
            className="border-border h-9 rounded-full border px-4 text-xs"
            isDisabled={isDisabled || isUploading}
            onPress={() => inputRef.current?.click()}
          >
            <PlusIcon width={14} height={14} />
            {value ? 'Replace' : 'Upload'}
          </Button>

          {value && (
            <Button
              variant="ghost"
              size="sm"
              className="text-muted hover:text-danger h-8 justify-start rounded-full px-2 text-xs"
              isDisabled={isDisabled || isUploading}
              onPress={() => {
                setLocalError(null)
                onChange(null)
              }}
            >
              <TrashIcon width={14} height={14} />
              Remove
            </Button>
          )}
        </div>
      </div>

      {description && !message && <p className="text-muted text-xs">{description}</p>}

      {message && (
        <p role="alert" className="text-danger flex items-center gap-1 text-xs">
          <AlertIcon width={13} height={13} />
          {message}
        </p>
      )}
    </div>
  )
}
